import * as Sentry from '@sentry/vue';
import axiosInstance from './axios.js';
import store from '../store/index.js';
import { requestTokenRefresh } from './token-refresh.js';

// Clock skew between browser and backend. A refresh that fails this close to
// the stored session expiry counts as "the session ran out", not as a fault.
const sessionExpirySkewMs = 60 * 1000;

/**
 * Thrown when the backend rejects the refresh cookie of a session that had
 * already run out: the session is simply over. `apiRequest` has cleared the
 * store and sent the browser to /login before throwing, so the caller only has
 * to stop what it was doing. Report it through `reportError`, which keeps this
 * expected end-of-session out of Sentry; every other refresh failure, a 401 on
 * a session that should still have been valid included, stays reportable.
 */
export class SessionExpiredError extends Error {
    constructor(cause) {
        super('Session expired: the backend rejected the refresh token');
        this.name = 'SessionExpiredError';
        this.cause = cause;
    }
}

/**
 * Report an error that came out of an api call. Use this instead of a bare
 * `Sentry.captureException` in catch blocks around `api.*`, so an expired
 * session no longer lands in Sentry as a crash of whatever view happened to be
 * loading. The breadcrumb from `apiRequest` keeps the logout visible in the
 * trail of any error that does get reported.
 */
export function reportError(error) {
    if (error instanceof SessionExpiredError) {
        return;
    }

    Sentry.captureException(error);
}

export async function refreshToken() {
    const { token, expiresAt } = await requestTokenRefresh();

    await store.dispatch('session/setAccessToken', {
        token,
        expiresAt,
    });

    return token;
}

export async function apiRequest(method, url, data = null, config = {}) {
    try {
        return await axiosInstance({ method, url, data, ...config });
    } catch (error) {
        // If 401 and not a refresh endpoint and not already retried
        if (error.response?.status === 401 && !url.includes('/token/refresh') && !config._retry) {
            try {
                const newToken = await refreshToken();

                // Merge headers properly to preserve existing headers
                const retryConfig = {
                    ...config,
                    method,
                    url,
                    data,
                    _retry: true,
                    headers: {
                        ...config.headers,
                        Authorization: `Bearer ${newToken}`,
                    },
                };

                return await axiosInstance(retryConfig);
            } catch (refreshError) {
                const refreshStatus = refreshError.response?.status ?? null;
                const expiresAt = store.getters['session/accessTokenExpiresAt'] ?? null;
                const msLeft = expiresAt === null ? null : expiresAt - Date.now();

                store.commit('CLEAR_STORE'); // Clear store before redirect
                await store.dispatch('session/clearSession');
                globalThis.location.href = '/login';

                Sentry.addBreadcrumb({
                    category: 'auth',
                    level: 'info',
                    message: 'Token refresh failed, session cleared and redirected to /login',
                    data: {
                        status: refreshStatus,
                        msLeft,
                        request: `${method.toUpperCase()} ${url}`,
                    },
                });

                // A 401 on a session that had already run out is the backend
                // saying the refresh cookie is spent: the redirect above is the
                // whole handling. Anything else stays a reportable failure, and
                // gets the context Sentry cannot collect by itself because the
                // refresh call runs on a bare axios instance without the
                // interceptor: a 5xx, a timeout or a dropped connection, but
                // also a 401 while the session should still have been valid,
                // which is what a refresh cookie consumed twice looks like.
                if (refreshStatus === 401 && msLeft !== null && msLeft <= sessionExpirySkewMs) {
                    throw new SessionExpiredError(refreshError);
                }

                Sentry.setContext('token_refresh', {
                    status: refreshStatus,
                    url: `${import.meta.env.VITE_API_ROOT}/api/token/refresh`,
                    message: refreshError.message,
                    request: `${method.toUpperCase()} ${url}`,
                    sessionMsLeft: msLeft,
                });

                throw refreshError;
            }
        }

        throw error;
    }
}

export default {
    get: (url, config) => apiRequest('get', url, null, config),
    post: (url, data, config) => apiRequest('post', url, data, config),
    put: (url, data, config) => apiRequest('put', url, data, config),
    delete: (url, config) => apiRequest('delete', url, null, config),
};
